import React, { useState } from "react";
import { webProjects, mobileProjects } from "../data/projects";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const fadeIn = (direction, type, delay, duration) => ({
  hidden: {
    x: direction === "left" ? 100 : direction === "right" ? -100 : 0,
    y: direction === "up" ? 100 : direction === "down" ? -100 : 0,
    opacity: 0,
  },
  show: {
    x: 0,
    y: 0,
    opacity: 1,
    transition: { type, delay, duration, ease: "easeOut" },
  },
});

const Projects = () => {
  const [activeTab, setActiveTab] = useState("web");
  const { t, i18n } = useTranslation();

  // Pick the right description based on current language
  const getDescription = (project) => {
    const lang = i18n.language?.slice(0, 2);
    if (lang === "ru") return project.description_ru;
    if (lang === "uz") return project.description_uz;
    return project.description_en;
  };

  const projects = activeTab === "web" ? webProjects : mobileProjects;
  const isMobileTab = activeTab === "mobile";

  return (
    <section id="projects">
      <motion.div variants={fadeIn("up", "tween", 0.2, 1)} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}>
        <p className="section__text__p1">{t("browse_recent")}</p>
        <h1 className="title">{t("projects_title")}</h1>
      </motion.div>
      
      <div className="project-tabs">
        <button 
          className={`tab-btn clickable ${activeTab === "web" ? "active" : ""}`} 
          onClick={() => setActiveTab("web")}
          style={{ position: "relative" }}
        >
          {activeTab === "web" && (
            <motion.div 
              layoutId="activeTabIndicator" 
              className="tab-active-bg" 
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
          )}
          <span style={{ position: "relative", zIndex: 1 }}>{t("web_dev")}</span>
        </button>
        <button 
          className={`tab-btn clickable ${activeTab === "mobile" ? "active" : ""}`} 
          onClick={() => setActiveTab("mobile")}
          style={{ position: "relative" }}
        >
          {activeTab === "mobile" && (
            <motion.div 
              layoutId="activeTabIndicator" 
              className="tab-active-bg" 
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
          )}
          <span style={{ position: "relative", zIndex: 1 }}>{t("mobile_apps")}</span>
        </button>
      </div>

      <div className="experience-details-container animated-grid" style={{ flexDirection: "column", alignItems: "center" }}>
        <AnimatePresence mode="wait">
          <motion.div 
            key={activeTab}
            className="about-containers mobile-layout-fix"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {projects.map((project, index) => (
              <motion.div 
                className="details-container color-container project-card card-hover" 
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                whileHover={{ y: -8, scale: 1.01 }}
              >
                <div className={`project-picture ${isMobileTab ? "project-picture-logo" : ""}`}>
                  {project.img ? (
                    <img src={project.img} alt={project.title} className={`project-img ${isMobileTab ? "project-logo-img" : ""}`} />
                  ) : (
                    <div className="project-img project-img-placeholder" aria-label={`${project.title} preview`}>
                      <span>{project.title}</span>
                    </div>
                  )}
                </div>
                <h2 className="experience-sub-title project-title">{project.title}</h2>
                {getDescription(project) && <p className="project-desc">{getDescription(project)}</p>}
                <div className="btn-container">
                  {project.github ? (
                    <motion.a 
                      href={project.github} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="btn btn-color-2 project-btn clickable"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t("github")}
                    </motion.a>
                  ) : isMobileTab ? (
                    <button type="button" className="btn btn-color-2 project-btn btn-disabled" disabled>
                      {t("github")}
                    </button>
                  ) : null}
                  {isMobileTab ? (
                    project.playMarket ? (
                      <motion.a 
                        href={project.playMarket} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="btn btn-color-2 project-btn clickable"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {t("play_market")}
                      </motion.a>
                    ) : (
                      <button type="button" className="btn btn-color-2 project-btn btn-disabled" disabled>
                        {t("in_process")}
                      </button>
                    )
                  ) : (
                    project.demo && (
                      <motion.a 
                        href={project.demo} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="btn btn-color-2 project-btn clickable"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {t("live_demo")}
                      </motion.a>
                    )
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Projects;
